import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import * as signalR from "@microsoft/signalr";
import { environment as env } from "../../environments/environment";
import { ToastService } from "./toast.service";
import { Globals } from "../models/lib/globals";

@Injectable({
  providedIn: "root",
})
export class SignalRService implements OnDestroy {
  hubConnection!: signalR.HubConnection;
  private workOrderUpdateCallbacks = new Set<(data: any) => void>();
  private registeredCallbacks = new Set<(data: any) => void>();

  private _connectionStatus = new BehaviorSubject<"online" | "offline">(
    "offline",
  );
  connectionStatus$ = this._connectionStatus.asObservable();

  constructor(
    private toastr: ToastService,
    private globalService: Globals,
  ) {}

  public startConnection = () => {
    setTimeout(() => {
      if (this.globalService.userLogged) {
        this.connectToSignalR();
      }
    }, 1000);
  };

  private connectToSignalR() {
    const token: string = localStorage.getItem("token") || "";
    if (token === null || token === "" || token === undefined) {
      return;
    }

    const messageUrl = env.MESSAGE_URL + "/msg";
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          const delays = [0, 2000, 5000, 10000, 20000, 30000, 60000, 120000];
          return delays[
            Math.min(retryContext.previousRetryCount, delays.length - 1)
          ];
        },
      })
      .withUrl(messageUrl, {
        accessTokenFactory: () => token,
      })
      .build();

    this.hubConnection.onreconnecting(() => {
      this._connectionStatus.next("offline");
    });

    this.hubConnection.onreconnected(() => {
      this._connectionStatus.next("online");
      if (this.workOrderUpdateCallbacks.size > 0) {
        this.hubConnection
          .invoke("SubscribeWorkOrderUpdate")
          .catch((err) => console.error(err));
      }
    });

    this.hubConnection.onclose(() => {
      this._connectionStatus.next("offline");
    });

    this.hubConnection
      .start()
      .then(() => {
        this._connectionStatus.next("online");
        this.addToasterMessageNotificationListener();
      })
      .catch((_err) => {
        this._connectionStatus.next("offline");
      });
  }

  public subscribeWorkOrderUpdate = (callback: any) => {
    this.workOrderUpdateCallbacks.add(callback);
    setTimeout(() => {
      if (!this.workOrderUpdateCallbacks.has(callback)) return;
      if (
        this.hubConnection === undefined ||
        this.hubConnection.state !== "Connected"
      ) {
        this.subscribeWorkOrderUpdate(callback);
        return;
      }
      this.hubConnection.invoke("SubscribeWorkOrderUpdate").catch((err) => {
        if (this.workOrderUpdateCallbacks.has(callback)) {
          this.subscribeWorkOrderUpdate(callback);
        }
      });
      this.addWorkOrderUpdateListener(callback);
    }, 1000);
  };

  public discconecctHub = () => {
    this._connectionStatus.next("offline");
    if (this.hubConnection !== undefined) {
      this.hubConnection.stop();
    }
  };

  public addToasterMessageNotificationListener = () => {
    this.hubConnection.on("ToasterMessage", (data) => {
      switch (data.status) {
        case 0:
          this.toastr.warning(data.message);
          break;
        case 1:
          this.toastr.success(data.message);
          break;
        case 2:
          this.toastr.error(data.message);
          break;
      }
    });
  };

  public addWorkOrderUpdateListener = (callback: any) => {
    if (this.registeredCallbacks.has(callback)) return;
    this.hubConnection.on("WorkOrderUpdate", callback);
    this.registeredCallbacks.add(callback);
  };

  public removeWorkOrderUpdateListener = (callback: (data: any) => void) => {
    this.workOrderUpdateCallbacks.delete(callback);
    this.registeredCallbacks.delete(callback);
    if (this.hubConnection) {
      this.hubConnection.off("WorkOrderUpdate", callback);
    }
  };

  ngOnDestroy() {
    this.discconecctHub();
  }
}
